import { useState, useMemo } from 'react';
import { useApp } from '@/contexts/AppContext';
import { addActivityLog } from '@/utils/activityLogStore';

interface ExpenseEntry {
  id: string;
  date: string;
  category: string;
  description: string;
  amount: number;
  payment_method: string;
  vendor: string;
  bill_no: string;
  created_at: string;
}

const EXPENSE_CATEGORIES = ['Salary', 'Electricity', 'Water', 'Maintenance', 'Vendor Payment', 'Cleaning', 'Kitchen Supplies', 'Marketing', 'Internet', 'Insurance', 'Taxes', 'Miscellaneous'];

const mockExpenses: ExpenseEntry[] = [
  { id: 'exp-001', date: '2026-05-01', category: 'Electricity', description: 'April electricity bill', amount: 18500, payment_method: 'bank_transfer', vendor: 'BESCOM', bill_no: 'BESCOM-0426', created_at: '2026-05-01T10:00:00Z' },
  { id: 'exp-002', date: '2026-05-05', category: 'Salary', description: 'Staff salary - April', amount: 120000, payment_method: 'bank_transfer', vendor: 'Staff Payroll', bill_no: 'SAL-0426', created_at: '2026-05-05T09:00:00Z' },
  { id: 'exp-003', date: '2026-05-08', category: 'Kitchen Supplies', description: 'Restocked kitchen items', amount: 8500, payment_method: 'cash', vendor: 'Fresh Foods Distributors', bill_no: 'FD-20260508', created_at: '2026-05-08T11:30:00Z' },
  { id: 'exp-004', date: '2026-05-10', category: 'Maintenance', description: 'Plumbing repair room 204', amount: 3500, payment_method: 'upi', vendor: 'QuickFix Services', bill_no: 'QF-0503', created_at: '2026-05-10T14:00:00Z' },
  { id: 'exp-005', date: '2026-05-12', category: 'Marketing', description: 'Google Ads campaign', amount: 15000, payment_method: 'card', vendor: 'Google Ads', bill_no: 'GA-20260512', created_at: '2026-05-12T16:00:00Z' },
  { id: 'exp-006', date: '2026-05-14', category: 'Water', description: 'Water tanker refill', amount: 4500, payment_method: 'cash', vendor: 'City Water Supply', bill_no: 'WT-0514', created_at: '2026-05-14T08:00:00Z' },
  { id: 'exp-007', date: '2026-05-15', category: 'Cleaning', description: 'Housekeeping supplies restock', amount: 6200, payment_method: 'upi', vendor: 'Bharat Hygiene Supplies', bill_no: 'BH-20260515', created_at: '2026-05-15T07:00:00Z' },
];

export default function ExpensesDaily() {
  const { addToast } = useApp();
  const [expenses, setExpenses] = useState<ExpenseEntry[]>(mockExpenses);
  const [showAdd, setShowAdd] = useState(false);
  const [newExpense, setNewExpense] = useState({ category: '', description: '', amount: '', payment_method: 'cash', vendor: '', bill_no: '', date: new Date().toISOString().slice(0, 10) });
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;

  const filtered = useMemo(() => {
    return expenses.filter((e) => {
      if (filterCategory !== 'all' && e.category !== filterCategory) return false;
      if (filterMonth !== 'all' && !e.date.startsWith(filterMonth)) return false;
      return true;
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [expenses, filterCategory, filterMonth]);

  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const totalExpenses = filtered.reduce((s, e) => s + e.amount, 0);
  const categoryTotals = useMemo(() => {
    const map: Record<string, number> = {};
    filtered.forEach((e) => {
      map[e.category] = (map[e.category] || 0) + e.amount;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [filtered]);

  const handleAddExpense = () => {
    if (!newExpense.category || !newExpense.description || !newExpense.amount) {
      addToast('error', 'Fill all required fields');
      return;
    }
    const entry: ExpenseEntry = {
      id: `exp-${Date.now()}`,
      date: newExpense.date,
      category: newExpense.category,
      description: newExpense.description,
      amount: Number(newExpense.amount),
      payment_method: newExpense.payment_method,
      vendor: newExpense.vendor,
      bill_no: newExpense.bill_no,
      created_at: new Date().toISOString(),
    };
    setExpenses([entry, ...expenses]);
    addActivityLog({
      user_id: 'usr-003',
      user_name: 'Reception',
      action: 'Expense Added',
      entity_type: 'expense',
      entity_id: entry.id,
      details: { amount: entry.amount, category: entry.category },
    });
    addToast('success', `Expense of Rs. ${entry.amount.toLocaleString('en-IN')} recorded`);
    setShowAdd(false);
    setNewExpense({ category: '', description: '', amount: '', payment_method: 'cash', vendor: '', bill_no: '', date: new Date().toISOString().slice(0, 10) });
  };

  const months = useMemo(() => {
    const set = new Set(expenses.map((e) => e.date.slice(0, 7)));
    return Array.from(set).sort().reverse();
  }, [expenses]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-navy-800">Daily Expenses</h1>
          <p className="text-navy-500 mt-1">Track all hotel operational expenses and vendor payments.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line mr-1" /> Add Expense
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Total Expenses</div>
          <div className="text-xl font-bold text-red-600">Rs. {totalExpenses.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Total Entries</div>
          <div className="text-xl font-bold text-navy-800">{filtered.length}</div>
        </div>
        {categoryTotals[0] && (
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="text-xs text-navy-500 mb-1">Top Category</div>
            <div className="text-xl font-bold text-navy-800">{categoryTotals[0][0]}</div>
            <div className="text-xs text-navy-400">Rs. {categoryTotals[0][1].toLocaleString('en-IN')}</div>
          </div>
        )}
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Categories</div>
          <div className="text-xl font-bold text-navy-800">{categoryTotals.length}</div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <h3 className="text-sm font-semibold text-navy-700 mb-3">Category Breakdown</h3>
        <div className="flex flex-wrap gap-2">
          {categoryTotals.map(([cat, amt]) => (
            <div key={cat} className="px-3 py-1.5 bg-navy-50 rounded-lg border border-navy-100 text-sm">
              <span className="font-medium text-navy-700">{cat}</span>
              <span className="text-red-600 ml-2">Rs. {amt.toLocaleString('en-IN')}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Category</label>
            <select value={filterCategory} onChange={(e) => { setFilterCategory(e.target.value); setCurrentPage(1); }} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="all">All Categories</option>
              {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Month</label>
            <select value={filterMonth} onChange={(e) => { setFilterMonth(e.target.value); setCurrentPage(1); }} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="all">All Months</option>
              {months.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy-50">
              <tr>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Date</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Category</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Description</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Vendor</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Method</th>
                <th className="text-right px-5 py-3 font-semibold text-navy-700">Amount</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((e) => (
                <tr key={e.id} className="border-t border-navy-100 hover:bg-navy-50/50 transition-colors">
                  <td className="px-5 py-3.5 text-navy-600 text-xs">{new Date(e.date).toLocaleDateString('en-IN')}</td>
                  <td className="px-5 py-3.5">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">{e.category}</span>
                  </td>
                  <td className="px-5 py-3.5 text-navy-800">{e.description}</td>
                  <td className="px-5 py-3.5 text-navy-500">{e.vendor || '-'}</td>
                  <td className="px-5 py-3.5"><span className="text-xs capitalize text-navy-600">{e.payment_method.replace('_', ' ')}</span></td>
                  <td className="px-5 py-3.5 text-right font-medium text-red-600">Rs. {e.amount.toLocaleString('en-IN')}</td>
                </tr>
              ))}
              {paginated.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-navy-400">No expenses found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-navy-100 flex items-center justify-between">
            <div className="text-xs text-navy-500">Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of {filtered.length}</div>
            <div className="flex gap-1">
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1.5 border border-navy-200 rounded-lg text-sm disabled:opacity-40 hover:bg-navy-50 cursor-pointer"><i className="ri-arrow-left-s-line" /></button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => setCurrentPage(p)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${currentPage === p ? 'bg-navy-700 text-white' : 'border border-navy-200 hover:bg-navy-50 text-navy-600'} cursor-pointer`}>{p}</button>
              ))}
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1.5 border border-navy-200 rounded-lg text-sm disabled:opacity-40 hover:bg-navy-50 cursor-pointer"><i className="ri-arrow-right-s-line" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Add Expense Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">Add Expense</h3>
              <button onClick={() => setShowAdd(false)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Date</label>
                <input type="date" value={newExpense.date} onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Category *</label>
                <select value={newExpense.category} onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
                  <option value="">Select...</option>
                  {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Description *</label>
                <input type="text" value={newExpense.description} onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" placeholder="e.g. Electricity bill for April" />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Amount (Rs.) *</label>
                <input type="number" value={newExpense.amount} onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Payment Method</label>
                <select value={newExpense.payment_method} onChange={(e) => setNewExpense({ ...newExpense, payment_method: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
                  <option value="cash">Cash</option><option value="upi">UPI</option><option value="card">Card</option><option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Vendor</label>
                <input type="text" value={newExpense.vendor} onChange={(e) => setNewExpense({ ...newExpense, vendor: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Bill No.</label>
                <input type="text" value={newExpense.bill_no} onChange={(e) => setNewExpense({ ...newExpense, bill_no: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button onClick={() => setShowAdd(false)} className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Cancel</button>
              <button onClick={handleAddExpense} className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer">Record Expense</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}